import type { EspaceRequest, EspaceSelection } from "./espace.types.js";

/**
 * Ce qu'une entreprise apprend sur elle-même sans avoir rien rempli.
 *
 * C'est la meilleure idée du dossier JobTeaser, et pour une raison qui n'a rien à
 * voir avec eux : dans un espace SANS COMPTE, personne ne remplit un formulaire de
 * profil. Une entreprise passe trois fois par an. Tout ce qui demande une saisie
 * restera vide — donc la seule information qui existera jamais est celle qui se
 * CALCULE depuis ce qu'elle a déjà fait.
 *
 * Le calcul vit dans `shared/` et pas dans le serveur : l'écran le refait à
 * l'identique sur les données qu'il a déjà, sans un aller-retour de plus, et un
 * test unique couvre les deux côtés.
 *
 * ⚠️ Aucune de ces mesures n'est une « vue » ni un « clic ». JobTeaser en affiche,
 * nous n'en avons pas : les offres vivent ici, les étudiants dans main-app, et
 * rien ne relie encore les deux. Afficher un compteur de vues à zéro, ou pire un
 * chiffre inventé, serait la seule façon de perdre la confiance qu'on construit.
 */

export type Habitudes = {
	/** Le nombre de dépôts sur lesquels tout ce qui suit est calculé. */
	base: number;
	/** Les contrats, du plus fréquent au moins fréquent. */
	contrats: { valeur: string; nombre: number }[];
	/** Les métiers déjà recrutés, dédoublonnés. */
	metiers: { valeur: string; nombre: number }[];
	/** Les mois de dépôt, pour voir la saisonnalité. 0 = janvier. */
	saison: { mois: number; nombre: number }[];
	/**
	 * Le délai moyen de réponse de l'école, en jours. `null` tant qu'aucune offre
	 * n'a été tranchée — un « 0 jour » sur zéro mesure serait un mensonge.
	 */
	delaiEcoleJours: number | null;
	/** Les profils reçus, et ceux qu'on a voulu rencontrer. */
	profilsRecus: number;
	profilsRetenus: number;
};

function compter(valeurs: (string | null)[]): { valeur: string; nombre: number }[] {
	const comptes = new Map<string, number>();
	for (const valeur of valeurs) {
		const propre = valeur?.trim();
		if (!propre) continue;
		comptes.set(propre, (comptes.get(propre) ?? 0) + 1);
	}
	return [...comptes.entries()]
		.map(([valeur, nombre]) => ({ valeur, nombre }))
		.sort((a, b) => b.nombre - a.nombre || a.valeur.localeCompare(b.valeur));
}

const JOUR_MS = 86_400_000;

export function habitudesDe(requests: EspaceRequest[], selections: EspaceSelection[]): Habitudes {
	const profils = selections.flatMap((selection) => selection.profiles);

	// Le délai ne se calcule QUE sur les offres tranchées, et seulement celles dont
	// on a les deux bornes. Compter une offre encore en attente comme « 0 jour »
	// tirerait la moyenne vers le bas à mesure que le retard s'accumule — le
	// contraire de ce que la mesure doit dire.
	const delais = requests
		.filter((request) => request.decidedAt)
		.map(
			(request) =>
				(new Date(request.decidedAt as string).getTime() - new Date(request.createdAt).getTime()) /
				JOUR_MS,
		)
		.filter((jours) => Number.isFinite(jours) && jours >= 0);

	return {
		base: requests.length,
		contrats: compter(requests.map((request) => request.contract)),
		metiers: compter(requests.map((request) => request.job)),
		saison: compter(requests.map((request) => String(new Date(request.createdAt).getMonth())))
			.map((entree) => ({ mois: Number(entree.valeur), nombre: entree.nombre }))
			.sort((a, b) => a.mois - b.mois),
		delaiEcoleJours:
			delais.length > 0
				? Math.round((delais.reduce((total, jours) => total + jours, 0) / delais.length) * 10) / 10
				: null,
		profilsRecus: profils.length,
		profilsRetenus: profils.filter((profile) => profile.decision === "oui").length,
	};
}

/**
 * La phrase qui résume, ou `null` quand il n'y a rien d'honnête à dire.
 *
 * Deux dépôts ne font pas une habitude. Sous ce seuil, l'écran n'affiche pas un
 * bloc à moitié vide : il n'affiche rien.
 */
export const HABITUDE_MINIMUM = 3;

export function resumeHabitudes(habitudes: Habitudes): string | null {
	if (habitudes.base < HABITUDE_MINIMUM) return null;

	const contrat = habitudes.contrats[0];
	if (!contrat) return null;

	const part = Math.round((contrat.nombre / habitudes.base) * 100);
	const metiers = habitudes.metiers.length;

	return part >= 60
		? `Vous recrutez surtout en ${contrat.valeur.toLowerCase()} (${part}\u202F% de vos ${habitudes.base} offres), sur ${metiers} ${metiers === 1 ? "métier" : "métiers"}.`
		: `Vos ${habitudes.base} offres se répartissent sur ${habitudes.contrats.length} types de contrat et ${metiers} ${metiers === 1 ? "métier" : "métiers"}.`;
}
